import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cross, Users, Globe } from 'lucide-react';
import { IMAGES } from '../mock/mock';
import Reveal from '../components/Reveal';
import ContactSection from '../components/ContactSection';

const PageHero = ({ label, title, image }) => (
  <section className="relative h-[52vh] min-h-[420px] flex items-center justify-center overflow-hidden">
    <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0b2027]/80 via-[#0b2027]/60 to-[#0b2027]/85" />
    <div className="relative text-center px-6">
      <Reveal>
        <p className="text-[#c9a24b] uppercase tracking-[0.36em] text-xs mb-5">{label}</p>
        <h1 className="font-serif-display italic text-white text-5xl md:text-7xl">{title}</h1>
      </Reveal>
    </div>
  </section>
);

const About = () => {
  const focus = [
    { icon: Cross, title: 'God', text: 'God is our primary focus because it is through knowing Him that we can have life and hope. We gather to worship Him, study His Word, listen to His Spirit, and grow in relationship with Him.' },
    { icon: Users, title: 'People', text: 'People from every race, culture, and background are important to God and to us. We walk in healthy relationships and live in community that encourages every person to live out their God-given purpose.' },
    { icon: Globe, title: 'Mission', text: 'As followers of Christ we fulfill the mission He gave us (Matthew 28:19-20). We work towards Godly change locally and abroad, serving East Knoxville and supporting missionaries around the world.' },
  ];

  return (
    <div>
      <PageHero label="Who We Are" title="About" image={IMAGES.congregation} />

      {/* VISION */}
      <section className="bg-[#f6f2ea] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-6">Our Vision</p>
            <p className="font-serif-display text-[#0f2e38] text-3xl md:text-5xl leading-snug mb-8">
              To lead people to find <span className="italic text-[#17565f]">hope</span> through a relationship with Jesus Christ.
            </p>
            <p className="text-[#3a4d54] text-lg leading-relaxed">
              1 Peter 1:3 says that real hope comes from being spiritually reborn through a relationship with God’s Son. This is how we can experience life both now and eternally. We exist to share this truth: There is hope… <span className="font-600 text-[#0f2e38]">Jesus.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* OUR FOCUS */}
      <section className="bg-[#0f2e38] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-16">
            <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-4">What Drives Us</p>
            <h2 className="font-serif-display text-white text-4xl md:text-6xl">Our Focus</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {focus.map((f, i) => (
              <Reveal key={f.title} delay={i * 120} className="bg-white/5 border border-white/10 p-10 hover:border-[#c9a24b]/50 hover:-translate-y-2 transition-all duration-500">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-[#c9a24b]/50 mb-6">
                  <f.icon className="h-7 w-7 text-[#c9a24b]" />
                </div>
                <h3 className="font-serif-display text-white text-3xl mb-4">{f.title}</h3>
                <p className="text-white/70 leading-relaxed">{f.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BELIEFS */}
      <section className="bg-[#f6f2ea] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <img src={IMAGES.worshipHands} alt="Beliefs" className="w-full h-[480px] object-cover" />
          </Reveal>
          <Reveal delay={140}>
            <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-5">Statement of Faith</p>
            <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-6xl mb-8">Our Beliefs</h2>
            <p className="text-[#3a4d54] text-lg leading-relaxed mb-6">
              This “Statement of Fundamental Truths” contains the 16 doctrines of the Assemblies of God — non-negotiable tenets of faith that all Assemblies of God churches adhere to.
            </p>
            <p className="text-[#3a4d54] text-lg leading-relaxed mb-10">
              Four of these — Salvation, the Baptism in the Holy Spirit, Divine Healing, and the Second Coming of Christ — are considered Cardinal Doctrines essential to reaching the world for Christ.
            </p>
            <a href="https://ag.org/Beliefs/Statement-of-Fundamental-Truths" target="_blank" rel="noopener noreferrer" className="btn-shine inline-flex items-center gap-3 bg-[#17565f] text-white hover:bg-[#0f2e38] px-8 py-4 uppercase tracking-[0.2em] text-sm font-600 transition-colors">
              Full Statement of Faith <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* OUR TEAM */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <Reveal className="text-center mb-16">
            <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-4">Leadership</p>
            <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-6xl">Our Team</h2>
          </Reveal>
          <Reveal className="grid md:grid-cols-2 gap-0 max-w-3xl mx-auto shadow-2xl">
            <img src={IMAGES.pastorCouple} alt="Zac & Jodi Povec" className="w-full h-full min-h-[360px] object-cover" />
            <div className="bg-[#0f2e38] p-12 flex flex-col justify-center">
              <h3 className="font-serif-display text-white text-3xl mb-2">Zac &amp; Jodi Povec</h3>
              <div className="h-px w-16 bg-[#c9a24b] my-4" />
              <p className="text-[#c9a24b] uppercase tracking-[0.22em] text-sm mb-6">Lead Pastors</p>
              <p className="text-white/70 leading-relaxed">
                Zac and Jodi have a heart to see people find hope in Jesus and grow into the purpose God has for them. They lead Living Hope with warmth, authenticity, and a deep love for the Knoxville community.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <ContactSection />
    </div>
  );
};

export default About;
